import { useQuery } from "@tanstack/react-query";
import api from "./api";

/**
 * Centralised React Query hooks for read-heavy shop endpoints.
 * Strategy:
 *  - staleTime: 5 min for /shop/items, /categories, /shop/trending
 *  - Keeps responses in memory across SPA navigation → back nav is instant
 *  - Refetch on window focus disabled (storefront is mostly idle browsing)
 *
 * Ingress strips HTTP Cache-Control on /api/*, so client-side React Query
 * caching is our primary perf lever for repeat reads.
 */

const FIVE_MIN = 5 * 60 * 1000;
const THIRTY_SEC = 30 * 1000;

export function useShopItems(category) {
    return useQuery({
        queryKey: ["shop-items", category || "All"],
        queryFn: async () => {
            const params = category && category !== "All" ? { category } : {};
            const r = await api.get("/shop/items", { params });
            return r.data;
        },
        // Stock + price changes frequently — keep the storefront feed snappy
        // by treating data as stale after 30s and re-fetching whenever the
        // shopper switches back to the tab.
        staleTime: THIRTY_SEC,
        gcTime: 10 * FIVE_MIN,
        refetchOnWindowFocus: true,
    });
}

export function useShopTrending(limit = 8) {
    return useQuery({
        queryKey: ["shop-trending", limit],
        queryFn: async () => (await api.get("/shop/trending", { params: { limit } })).data,
        staleTime: FIVE_MIN,
        refetchOnWindowFocus: false,
    });
}

export function useCategories() {
    return useQuery({
        queryKey: ["categories"],
        queryFn: async () => (await api.get("/categories")).data,
        staleTime: FIVE_MIN,
        refetchOnWindowFocus: false,
    });
}

export function useDepartments() {
    return useQuery({
        queryKey: ["departments"],
        queryFn: async () => (await api.get("/departments")).data,
        staleTime: FIVE_MIN,
        refetchOnWindowFocus: false,
    });
}

export function useItem(id) {
    return useQuery({
        queryKey: ["item", id],
        queryFn: async () => (await api.get(`/items/${id}`)).data,
        enabled: !!id,
        // PDP — stock + sale_price changes often; refetch on focus so two
        // tabs / users see consistent availability.
        staleTime: 15 * 1000,
        refetchOnWindowFocus: true,
    });
}

export function useShopSearch(q) {
    const query = (q || "").trim();
    return useQuery({
        queryKey: ["shop-search", query],
        queryFn: async () => (await api.get("/shop/search", { params: { q: query, limit: 30 } })).data,
        enabled: query.length >= 2,
        staleTime: 30 * 1000,
        refetchOnWindowFocus: false,
        placeholderData: (prev) => prev, // keep old results visible while typing (no flash)
    });
}

export function usePublicSettings() {
    return useQuery({
        queryKey: ["public-settings"],
        queryFn: async () => (await api.get("/settings/public")).data,
        staleTime: 30 * FIVE_MIN, // 2.5h — rarely changes
        refetchOnWindowFocus: false,
    });
}
